import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CHAT_WS_URL } from "../../api";
import "./ChatFab.css";

export const DEFAULT_CHAT_STORAGE_KEY = "dashboard-chat-session-id";
const DEFAULT_PINNED_MESSAGE = "Ask about the selected area, risk patterns, prevention planning, or dashboard layers.";
const COMING_SOON_MESSAGE = "The chat feature is coming soon.";
function buildDefaultMessages(pinnedMessage = DEFAULT_PINNED_MESSAGE) {
  return [
    {
      role: "assistant",
      content: pinnedMessage,
    },
  ];
}

function nextMessageId() {
  return `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function readStoredSessionId(storageKey) {
  try {
    return window.localStorage.getItem(storageKey) || "";
  } catch {
    return "";
  }
}

function storeSessionId(storageKey, sessionId) {
  try {
    window.localStorage.setItem(storageKey, sessionId);
  } catch {
    // Ignore storage failures; the backend can always create a fresh session.
  }
}

export function clearStoredChatSession(storageKey = DEFAULT_CHAT_STORAGE_KEY) {
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // Ignore storage failures during logout cleanup.
  }
}

function ChatFab({
  context,
  storageKey = DEFAULT_CHAT_STORAGE_KEY,
  title = "Dashboard Assistant",
  placeholder = "Ask about this dashboard",
  pinnedMessage = DEFAULT_PINNED_MESSAGE,
  onClearContext = null,
  showContextInfo = true,
  actions = [],
  enabled = true,
}) {
  const socketRef = useRef(null);
  const pendingMessageRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [connected, setConnected] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [connectionAttempted, setConnectionAttempted] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const defaultMessages = useMemo(() => buildDefaultMessages(pinnedMessage), [pinnedMessage]);
  const defaultPinnedMessageRef = useRef(pinnedMessage);
  const [messages, setMessages] = useState(() => buildDefaultMessages(pinnedMessage));

  const contextPayload = useMemo(() => context || {}, [context]);
  const sessionStorageKey = storageKey;
  const selectedContextTitle = contextPayload.selectedContextTitle;
  const hasSelectedContext = Boolean(contextPayload.selectedContext);
  const availableActions = Array.isArray(actions)
    ? actions.filter((action) => action && action.label && typeof action.onClick === "function")
    : [];
  useEffect(() => {
    setMessages((current) => {
      const previousPinnedMessage = defaultPinnedMessageRef.current;
      defaultPinnedMessageRef.current = pinnedMessage;

      if (current.length === 0) return defaultMessages;
      if (current[0]?.role === "assistant" && current[0]?.content === previousPinnedMessage) {
        return [{ ...current[0], content: pinnedMessage }, ...current.slice(1)];
      }
      return current;
    });
  }, [defaultMessages, pinnedMessage]);

  const sendJson = useCallback((payload) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(payload));
    return true;
  }, []);

  const connect = useCallback(() => {
    if (!enabled) return;

    if (socketRef.current && socketRef.current.readyState <= WebSocket.OPEN) {
      return;
    }

    setError("");
    setConnectionAttempted(true);
    setAuthenticated(false);
    const socket = new WebSocket(CHAT_WS_URL);
    socketRef.current = socket;

    socket.onopen = () => {
      setConnected(true);
      sendJson({
        type: "auth",
        sessionId: readStoredSessionId(sessionStorageKey),
        context: contextPayload,
      });
    };

    socket.onmessage = (event) => {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }

      if (payload.type === "auth_ok") {
        setAuthenticated(true);
        if (payload.sessionId) {
          setSessionId(payload.sessionId);
          storeSessionId(sessionStorageKey, payload.sessionId);
        }
        if (Array.isArray(payload.messages)) {
          const history = payload.messages.length > 0 ? payload.messages : defaultMessages;
          const pending = pendingMessageRef.current;
          setMessages(
            pending
              ? [...history, { id: `${pending.id}-user`, role: "user", content: pending.message }]
              : history
          );
        }
        if (pendingMessageRef.current) {
          sendJson(pendingMessageRef.current);
          pendingMessageRef.current = null;
        }
        return;
      }

      if (payload.type === "stream_start") {
        setStreaming(true);
        return;
      }

      if (payload.type === "chunk") {
        setMessages((current) => {
          const next = [...current];
          const last = next[next.length - 1];
          if (last?.id === payload.id && last.role === "assistant") {
            next[next.length - 1] = { ...last, content: `${last.content}${payload.content || ""}` };
          } else {
            next.push({ id: payload.id, role: "assistant", content: payload.content || "" });
          }
          return next;
        });
        return;
      }

      if (payload.type === "done") {
        setStreaming(false);
        return;
      }

      if (payload.type === "cleared") {
        if (payload.sessionId) {
          setSessionId(payload.sessionId);
          storeSessionId(sessionStorageKey, payload.sessionId);
        }
        setMessages(defaultMessages);
        setError("");
        return;
      }

      if (payload.type === "error") {
        setStreaming(false);
        setError(payload.error || "Chat request failed");
      }
    };

    socket.onerror = () => {
      pendingMessageRef.current = null;
      setStreaming(false);
      setError(`Chat connection failed at ${CHAT_WS_URL}. Check that the Python backend is running and rebuild the UI if env vars changed.`);
    };

    socket.onclose = () => {
      pendingMessageRef.current = null;
      setConnected(false);
      setAuthenticated(false);
      setStreaming(false);
      socketRef.current = null;
    };
  }, [contextPayload, defaultMessages, enabled, sendJson, sessionStorageKey]);

  useEffect(() => {
    if (open && enabled) connect();
  }, [connect, enabled, open]);

  useEffect(() => {
    return () => {
      if (socketRef.current) socketRef.current.close();
    };
  }, []);

  const clearHistory = useCallback(() => {
    if (streaming) return;
    const clearedContextPayload = contextPayload;
    onClearContext?.();
    setError("");
    if (!connected || !authenticated) {
      setMessages(defaultMessages);
      return;
    }
    if (!sendJson({ type: "clear", sessionId, context: clearedContextPayload })) {
      setError("Unable to clear chat history while disconnected");
    }
  }, [authenticated, connected, contextPayload, defaultMessages, onClearContext, sendJson, sessionId, streaming]);

  const submitMessage = useCallback((event) => {
    event.preventDefault();
    const message = input.trim();
    if (!message || streaming) return;

    const id = nextMessageId();
    const payload = { type: "message", id, message, context: contextPayload };
    setMessages((current) => [...current, { id: `${id}-user`, role: "user", content: message }]);
    setInput("");
    setError("");

    if (!enabled) {
      setMessages((current) => [
        ...current,
        { id: `${id}-assistant`, role: "assistant", content: COMING_SOON_MESSAGE },
      ]);
      return;
    }

    if (!connected || !authenticated) {
      pendingMessageRef.current = payload;
      connect();
      return;
    }

    if (!sendJson(payload)) {
      pendingMessageRef.current = payload;
      connect();
    }
  }, [authenticated, connect, connected, contextPayload, enabled, input, sendJson, streaming]);

  const handleActionClick = useCallback((action) => {
    action.onClick();
    if (action.closeOnClick) setOpen(false);
  }, []);

  return (
    <div className={`chat-fab ${open ? "chat-fab-open" : ""}`}>
      {open && (
        <section className="chat-panel" aria-label="Dashboard assistant">
          <div className="chat-panel-header">
            <div>
              <h2>{title}</h2>
              <p>{!enabled ? "Coming soon" : connected ? authenticated ? "Connected" : "Starting session" : connectionAttempted ? "Disconnected" : "Ready"}</p>
            </div>
            <div className="chat-header-actions">
              <button
                type="button"
                className="chat-text-button"
                onClick={clearHistory}
                disabled={streaming}
              >
                Clear
              </button>
              <button type="button" className="chat-icon-button" onClick={() => setOpen(false)} aria-label="Close chat">
                x
              </button>
            </div>
          </div>


          {showContextInfo && (
            <div className={"chat-context-chip " + (hasSelectedContext ? "selected" : "empty")}>
              <span>Chat context</span>
              <strong>{hasSelectedContext ? selectedContextTitle : "Current dashboard tab"}</strong>
            </div>
          )}

          {availableActions.length > 0 && (
            <div className="chat-actions-compartment" aria-label="Assistant actions">
              <span>Actions</span>
              <div className="chat-action-list">
                {availableActions.map((action) => (
                  <button
                    key={action.id || action.label}
                    type="button"
                    className="chat-action-button"
                    onClick={() => handleActionClick(action)}
                    disabled={action.disabled}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="chat-messages" role="log" aria-live="polite">
            {messages.map((message, index) => (
              <div key={message.id || index} className={`chat-message chat-message-${message.role}`}>
                {message.content}
              </div>
            ))}
            {streaming && <div className="chat-thinking">Streaming response...</div>}
          </div>

          {error && <div className="chat-error">{error}</div>}

          <form className="chat-input-row" onSubmit={submitMessage}>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={placeholder}
              disabled={streaming}
            />
            <button type="submit" disabled={streaming || !input.trim()} aria-label="Send chat message">
              &gt;
            </button>
          </form>
        </section>
      )}

      <button
        type="button"
        className="chat-fab-button"
        onClick={() => {
          setOpen((value) => !value);
          if (!open) setConnectionAttempted(false);
        }}
        aria-expanded={open}
        aria-label={open ? "Close dashboard assistant" : "Open dashboard assistant"}
      >
        {open ? "x" : "?"}
      </button>
    </div>
  );
}

export default ChatFab;
